/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';



import { useField } from 'react-final-form';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */





import FieldContext from '../../../../context/FieldContext';


import OpmsRowErrors from './OpmsStaticRowErrors';

import StaticRowErrorContext from '../../../../context/StaticRowErrorContext';

import LookupsContext from '../../../../context/LookupsContext';

import TableCell from '@material-ui/core/TableCell/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { FormSubscription } from 'final-form';
import ConditionalContext from '../../../../context/ConditionalContext';
import LookupContext from '../../../../context/LookupContext';
import StaticGridContext from '../../../../context/StaticGridContext';
import OpmsConditional, { IOpmsContionParams } from '../../OpmsConditional';
import { pascalToCamelCase } from '../../OpmsInput';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
interface IOpmsStaticRowProps<FormType extends object = any>  {
    cells: Array<IOpmsStaticRow<FormType>>
}

export interface IOpmsStaticRow<FormType extends object = any> {
    columnName: string;
    fieldName: string | null;
    condition?: (parameters: IOpmsContionParams<FormType>) => boolean;
    subscription?: FormSubscription;
    content: React.ReactNode;
}


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const OpmsStaticCell: React.FunctionComponent<IOpmsStaticRow> = (props) => {
    if (!props.fieldName) {
        return null;
    }

    return (
        <InnerOpmsStaticCell {...props} />
    )
}

const InnerOpmsStaticCell: React.FC<IOpmsStaticRow> = ({
    content,
    columnName,
    fieldName: name,
    children
}) => {
    const lookups = React.useContext(LookupsContext);

    const lookup = React.useMemo(() => {
        return lookups?.find(l => l.propertyName === name) ?? null;
    }, [name, lookups])

    const conditionalResult = React.useContext(ConditionalContext)

    const fieldName = React.useMemo(() => pascalToCamelCase(name!), [name]);

    const { input: { onChange } } = useField(fieldName, {});

    React.useEffect(() => {
        if (conditionalResult?.result === false) {
            onChange(undefined);
        }
    }, [onChange, conditionalResult?.result])

    if (conditionalResult?.mode === 'Show' && conditionalResult?.result === false) {
        return null as any;
    }

    return (
        <TableCell>
            <LookupContext.Provider value={lookup}>
                <FieldContext.Provider value={name!}>
                    {content}
                </FieldContext.Provider>
            </LookupContext.Provider>
        </TableCell>
    )
}

function OpmsStaticRow<FormType extends object = any>({
    cells
}: IOpmsStaticRowProps<FormType>) {

    const [errorsExpanded, setErrorsExpanded] = React.useState(true);

    const rowErrorState = React.useMemo(() => {
        return {
            expanded: errorsExpanded,
            setExpanded: setErrorsExpanded
        }
    }, [errorsExpanded, setErrorsExpanded]);

    const headers = React.useContext(StaticGridContext);

    const calculatedCells: Array<IOpmsStaticRow<FormType>> = React.useMemo(() => {
        return headers.map(header => {
            const cell = cells.find(c => header.columnName === c.columnName);

            if (cell) {
                return cell;
            }

            return {
                columnName: header.columnName,
                fieldName: null,
                content: null
            }
        });
    }, [headers, cells])

    const calculatedFields: string[] = React.useMemo(() => {
        return Array.from(new Set(calculatedCells.filter(c => !!c.fieldName).map(c => c.fieldName) as string[]));
    }, [calculatedCells])

    return (
        <StaticRowErrorContext.Provider value={rowErrorState}>
            <TableRow>
                {
                    calculatedCells.map(cell => {
                        if (!cell.fieldName) {
                            return (
                                <TableCell
                                    key={cell.columnName}
                                />
                            );
                        }

                        if (cell.condition) {
                            return (
                                <OpmsConditional
                                    key={cell.columnName}
                                    condition={cell.condition}
                                    subscription={cell.subscription ?? {}}
                                    mode="Enable"
                                >
                                    <OpmsStaticCell
                                        {...cell}
                                    />
                                </OpmsConditional>
                            );
                        }

                        return (
                            <OpmsStaticCell
                                key={cell.columnName}
                                {...cell}
                            />
                        );
                    })
                }
            </TableRow>
            <OpmsRowErrors fields={calculatedFields} columnCount={calculatedCells.length} />
        </StaticRowErrorContext.Provider>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsStaticRow;
